import { Alert, Anchor, Paper, Progress, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../../auth/auth-context';
import { ErrorAlert } from '../../components/ErrorAlert';
import { PageHeader } from '../../components/PageHeader';
import { formatNumber } from '../../lib/format';
import { useDashboard } from '../admin/api';
import { BillingRulesWarning } from '../billing/BillingRulesWarning';
import { QuickActions } from './QuickActions';

function Kpi({
  label,
  value,
  footer,
  to,
}: {
  label: string;
  value: ReactNode;
  footer?: ReactNode;
  to?: string;
}) {
  const content = (
    <Paper withBorder p="md" radius="md" h="100%">
      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
        {label}
      </Text>
      <Text fz={34} fw={800} lh={1.15} className="tabular-nums">
        {value}
      </Text>
      {footer}
    </Paper>
  );
  return to ? (
    <Anchor component={Link} to={to} underline="never" c="inherit">
      {content}
    </Anchor>
  ) : (
    content
  );
}

/** Panel principal: situación del almacén y accesos rápidos. */
export function DashboardPage() {
  const { can } = useAuth();
  const { data, error } = useDashboard();
  const stats = data?.stats;

  return (
    <Stack>
      <PageHeader title="Inicio" />
      <ErrorAlert error={error} />
      {data && stats && (
        <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="sm">
          <Kpi
            label="Palets actuales"
            value={formatNumber(stats.palletsInStock)}
            to="/inventario"
          />
          <Kpi
            label="Ubicaciones"
            value={`${formatNumber(stats.locations.occupied)} / ${formatNumber(stats.locations.usable)}`}
            to="/mapa"
          />
          <Kpi
            label="Ocupación"
            value={`${stats.occupancyPct.toLocaleString('es-ES')} %`}
            footer={
              <Progress
                value={stats.occupancyPct}
                mt={6}
                color={stats.occupancyPct > 90 ? 'red' : 'blue'}
              />
            }
          />
          <Kpi
            label="Clientes con mercancía"
            value={formatNumber(stats.clientsWithStock)}
            to="/inventario"
          />
          <Kpi label="Entradas hoy" value={formatNumber(data.entriesToday)} />
          <Kpi label="Salidas hoy" value={formatNumber(data.exitsToday)} />
        </SimpleGrid>
      )}
      {data && stats && stats.pendingLocation > 0 && (
        <Alert color="orange" icon={<IconAlertTriangle />}>
          Hay <b>{stats.pendingLocation}</b> palet(s) pendientes de ubicar.{' '}
          {can('pallets.locate') && (
            <Anchor component={Link} to="/ubicar">
              Ubicar ahora
            </Anchor>
          )}
        </Alert>
      )}
      {data && data.openOutboundOrders > 0 && (
        <Alert color="yellow">
          {data.openOutboundOrders} orden(es) de salida en preparación.{' '}
          <Anchor component={Link} to="/salidas">
            Ver salidas
          </Anchor>
        </Alert>
      )}
      {data && !data.billingRulesConfirmed && can('billing.read') && <BillingRulesWarning />}
      <QuickActions />
    </Stack>
  );
}
